// Admin Portal API Slice - Fleet Management, Operations, Testing, User Management
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const adminApiSlice = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'DeviceGroup',
    'Vehicle',
    'Device',
    'Driver',
    'Geofences',
    'User',
    'UserGroup',
    'SalesReps',
    'Branches',
    'Technicians',
    'Task',
    'Test',
  ],
  endpoints: (builder) => ({
    // ========== FLEET MANAGEMENT ENDPOINTS ==========
    fetchDeviceGroups: builder.query({
      query: () => 'fleet/device-groups/',
      providesTags: ['DeviceGroup'],
    }),

    fetchVehiclesForGroups: builder.query({
      query: () => 'fleet/vehicles/',
      providesTags: ['Vehicle'],
    }),

    fetchDevicesForVehicles: builder.query({
      query: () => 'fleet/devices/',
      providesTags: ['Device'],
    }),

    updateClient: builder.mutation({
      query: ({ group_id, data }) => ({
        url: `fleet/device-groups/${group_id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['DeviceGroup'],
    }),

    deleteClient: builder.mutation({
      query: (group_id) => ({
        url: `fleet/device-groups/${group_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceGroup'],
    }),

    moveDeviceToGroup: builder.mutation({
      query: ({ device_id, new_group_id }) => ({
        url: `fleet/move-device/${device_id}/`,
        method: 'POST',
        body: { new_group_id },
      }),
      invalidatesTags: ['DeviceGroup', 'Device'],
    }),

    createNewDevice: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/create-new-device/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),

    createGroup: builder.mutation({
      query: (groupData) => ({
        url: 'fleet/create-group/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DeviceGroup'],
    }),

    assignDeviceToGroup: builder.mutation({
      query: (payload) => ({
        url: 'fleet/assign-device-to-group/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),

    updateVehicle: builder.mutation({
      query: (vehicleData) => ({
        url: 'fleet/update-vehicles/',
        method: 'POST',
        body: vehicleData,
      }),
      invalidatesTags: ['Vehicle', 'Device'],
    }),

    updateDevice: builder.mutation({
      query: (deviceData) => ({
        url: 'fleet/update-device/',
        method: 'POST',
        body: deviceData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),

    localAddDevices: builder.mutation({
      query: (devices) => ({
        url: 'fleet/create-device/',
        method: 'POST',
        body: devices,
      }),
      invalidatesTags: ['Device'],
    }),

    createDeviceSameGroup: builder.mutation({
      query: (payload) => ({
        url: 'fleet/create-device-same-group/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device'],
    }),

    associateDeviceWithVehicle: builder.mutation({
      query: (payload) => ({
        url: 'fleet/associate-device-with-vehicle/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),

    createVehicle: builder.mutation({
      query: (newVehicleData) => ({
        url: 'fleet/vehicles/create/',
        method: 'POST',
        body: newVehicleData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Vehicle'],
    }),

    assignDeviceToVehicle: builder.mutation({
      query: (payload) => ({
        url: 'fleet/devices/assign-to-vehicle/',
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),

    // ========== GEOFENCE MANAGEMENT ==========
    fetchGeofences: builder.query({
      query: () => 'fleet/geofences/',
      providesTags: ['Geofences'],
    }),

    createGeofence: builder.mutation({
      query: (data) => ({
        url: 'fleet/geofences/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Geofences'],
    }),

    updateGeofence: builder.mutation({
      query: ({ id, data }) => ({
        url: `fleet/geofences/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Geofences'],
    }),

    deleteGeofence: builder.mutation({
      query: (id) => ({
        url: `fleet/geofences/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Geofences'],
    }),

    fetchVehicleEventHistory: builder.query({
      query: ({ vehicleId, eventType, limit = 3 }) => {
        if (!vehicleId) {
          // Return limited events instead of unlimited (page_size: 0 was causing performance issues)
          return { url: 'monitoring/events/list/', params: { page_size: 50 } };
        }
        let url = `fleet/vehicles/${vehicleId}/event-history/`;
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (limit) params.append('limit', limit);
        const queryString = params.toString();
        if (queryString) url += `?${queryString}`;
        return url;
      },
      providesTags: ['Event'],
    }),

    // ========== OPERATIONS ENDPOINTS ==========
    fetchBranches: builder.query({
      query: () => 'operations/branches/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Branches'],
    }),

    fetchBranch: builder.query({
      query: (id) => `operations/branches/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Branches', id }],
    }),

    createBranch: builder.mutation({
      query: (data) => ({
        url: 'operations/branches/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Branches'],
    }),

    updateBranch: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/branches/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Branches', id }, 'Branches'],
    }),

    deleteBranch: builder.mutation({
      query: (id) => ({
        url: `operations/branches/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Branches'],
    }),

    // ========== TECHNICIAN MANAGEMENT ==========
    fetchTechnicians: builder.query({
      query: () => 'operations/technicians/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Technicians'],
    }),

    createTechnician: builder.mutation({
      query: (data) => ({
        url: 'operations/technicians/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Technicians'],
    }),

    updateTechnician: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/technicians/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Technicians'],
    }),

    deleteTechnician: builder.mutation({
      query: (id) => ({
        url: `operations/technicians/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Technicians'],
    }),

    // ========== SALES REP MANAGEMENT ==========
    fetchSalesReps: builder.query({
      query: () => 'operations/sales-reps/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['SalesReps'],
    }),

    fetchSalesRep: builder.query({
      query: (id) => `operations/sales-reps/${id}/`,
      providesTags: (result, error, id) => [{ type: 'SalesReps', id }],
    }),

    createSalesRep: builder.mutation({
      query: (data) => ({
        url: 'operations/sales-reps/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SalesReps'],
    }),

    updateSalesRep: builder.mutation({
      query: ({ id, data }) => ({
        url: `operations/sales-reps/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'SalesReps', id }, 'SalesReps'],
    }),

    deleteSalesRep: builder.mutation({
      query: (id) => ({
        url: `operations/sales-reps/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesReps'],
    }),

    // ========== TASK MANAGEMENT ==========
    getTasks: builder.query({
      query: () => 'operations/tasks/',
      transformResponse: (response) => response?.results || response,
      providesTags: ['Task'],
    }),

    getTask: builder.query({
      query: (id) => `operations/tasks/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    addTask: builder.mutation({
      query: (task) => ({
        url: 'operations/tasks/',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    updateTask: builder.mutation({
      query: ({ id, ...task }) => ({
        url: `operations/tasks/${id}/`,
        method: 'PUT',
        body: task,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Task', id }],
    }),

    deleteTask: builder.mutation({
      query: (id) => ({
        url: `operations/tasks/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),

    // ========== TESTING ENDPOINTS ==========
    fetchTests: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('search', params.search);
        if (params.status) queryParams.append('status', params.status);
        if (params.test_type) queryParams.append('test_type', params.test_type);
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        const queryString = queryParams.toString();
        return `testing/test-list/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Test'],
    }),

    fetchTestDetail: builder.query({
      query: ({ testId, testType }) => `testing/test-detail/${testId}/${testType}/`,
      providesTags: (result, error, { testId, testType }) => [
        { type: 'Test', id: `${testType}-${testId}` },
      ],
    }),

    createNewInstallTest: builder.mutation({
      query: (installTestData) => ({
        url: 'testing/new-install-test/',
        method: 'POST',
        body: installTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle', 'DeviceGroup'],
    }),

    createNewReInstallTest: builder.mutation({
      query: (reinstallTestData) => ({
        url: 'testing/re-install-test/',
        method: 'POST',
        body: reinstallTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle', 'DeviceGroup', 'Test'],
    }),

    createNewRepairTest: builder.mutation({
      query: (repairTestData) => ({
        url: 'testing/new-repair-test/',
        method: 'POST',
        body: repairTestData,
      }),
      invalidatesTags: ['Device'],
    }),

    createNewDeInstallTest: builder.mutation({
      query: (deinstallTestData) => ({
        url: 'testing/new-deinstall-test/',
        method: 'POST',
        body: deinstallTestData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Device', 'Vehicle'],
    }),

    // ========== USER & GROUP MANAGEMENT ==========
    fetchUsers: builder.query({
      query: () => '/auth/users/',
      providesTags: ['User'],
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: '/auth/users/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    updateUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `/auth/users/${userId}/`,
        method: 'PUT',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    fetchUserGroups: builder.query({
      query: () => '/auth/groups/',
      providesTags: ['UserGroup'],
    }),

    resetPassword: builder.mutation({
      query: ({ username, email, newPassword }) => ({
        url: '/auth/reset-password/',
        method: 'POST',
        body: { username, email, newPassword },
      }),
    }),

    createUserGroup: builder.mutation({
      query: (groupData) => ({
        url: '/auth/groups/',
        method: 'POST',
        body: groupData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UserGroup'],
    }),

    updateUserGroup: builder.mutation({
      query: ({ groupId, ...groupData }) => ({
        url: `/auth/groups/${groupId}/`,
        method: 'PUT',
        body: groupData,
      }),
      invalidatesTags: ['UserGroup'],
    }),

    deleteUserGroup: builder.mutation({
      query: (groupId) => ({
        url: `/auth/groups/${groupId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserGroup'],
    }),

    // ========== DRIVER MANAGEMENT ==========
    fetchDrivers: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append('status', params.status);
        if (params.group_id) queryParams.append('group_id', params.group_id);
        if (params.search) queryParams.append('search', params.search);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        const queryString = queryParams.toString();
        return `fleet/client/drivers/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Driver'],
    }),

    fetchDriverDetails: builder.query({
      query: (driverId) => `fleet/client/drivers/${driverId}/`,
      providesTags: (result, error, driverId) => [{ type: 'Driver', id: driverId }],
    }),

    createDriver: builder.mutation({
      query: (driverData) => ({
        url: 'fleet/client/drivers/create/',
        method: 'POST',
        body: driverData,
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    updateDriver: builder.mutation({
      query: ({ driverId, ...data }) => ({
        url: `fleet/client/drivers/${driverId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    deleteDriver: builder.mutation({
      query: (driverId) => ({
        url: `fleet/client/drivers/${driverId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    createDriverAssignment: builder.mutation({
      query: (data) => ({
        url: 'fleet/client/driver-assignments/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    deleteDriverAssignment: builder.mutation({
      query: (assignmentId) => ({
        url: `fleet/client/driver-assignments/${assignmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    fetchAvailableDrivers: builder.query({
      query: (vehicleId) => `fleet/client/vehicles/${vehicleId}/available-drivers/`,
      providesTags: ['Driver'],
    }),

    // ========== IMPERSONATION ==========
    fetchUsersByPortal: builder.query({
      query: (portal) => `v1/rbac/super-admin/users/by_portal/?portal=${portal}`,
      providesTags: ['User'],
    }),

    impersonateUser: builder.mutation({
      query: ({ userId, targetPortal }) => ({
        url: 'v1/rbac/auth/impersonate/',
        method: 'POST',
        body: { user_id: userId, target_portal: targetPortal },
      }),
    }),
  }),
});

// Export hooks for Admin Portal components
export const {
  // Fleet Management
  useFetchDeviceGroupsQuery,
  useFetchVehiclesForGroupsQuery,
  useFetchDevicesForVehiclesQuery,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useMoveDeviceToGroupMutation,
  useCreateNewDeviceMutation,
  useCreateGroupMutation,
  useAssignDeviceToGroupMutation,
  useUpdateVehicleMutation,
  useUpdateDeviceMutation,
  useLocalAddDevicesMutation,
  useCreateDeviceSameGroupMutation,
  useAssociateDeviceWithVehicleMutation,
  useCreateVehicleMutation,
  useAssignDeviceToVehicleMutation,
  useFetchVehicleEventHistoryQuery,

  // Geofence Management
  useFetchGeofencesQuery,
  useCreateGeofenceMutation,
  useUpdateGeofenceMutation,
  useDeleteGeofenceMutation,

  // Operations - Branches
  useFetchBranchesQuery,
  useFetchBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,

  // Operations - Technicians
  useFetchTechniciansQuery,
  useCreateTechnicianMutation,
  useUpdateTechnicianMutation,
  useDeleteTechnicianMutation,

  // Operations - Sales Reps
  useFetchSalesRepsQuery,
  useFetchSalesRepQuery,
  useCreateSalesRepMutation,
  useUpdateSalesRepMutation,
  useDeleteSalesRepMutation,

  // Task Management
  useGetTasksQuery,
  useGetTaskQuery,
  useAddTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,

  // Testing
  useFetchTestsQuery,
  useFetchTestDetailQuery,
  useLazyFetchTestDetailQuery,
  useCreateNewInstallTestMutation,
  useCreateNewReInstallTestMutation,
  useCreateNewRepairTestMutation,
  useCreateNewDeInstallTestMutation,

  // User & Group Management
  useFetchUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
  useFetchUserGroupsQuery,
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,

  // Driver Management
  useFetchDriversQuery,
  useFetchDriverDetailsQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
  useCreateDriverAssignmentMutation,
  useDeleteDriverAssignmentMutation,
  useFetchAvailableDriversQuery,
  useLazyFetchAvailableDriversQuery,

  // Impersonation
  useFetchUsersByPortalQuery,
  useImpersonateUserMutation,
} = adminApiSlice;
